import { Socket } from "socket.io-client";
import { socketDebug, socketLog } from "./debug";
import { isRealtimeEntity, type RealtimeEntity } from "./dashboardEvents";

export interface Attachment {
  fileKey: string;
  fileName: string;
  fileType: string;
  key?: string;
}

export interface MessagePayload {
  uuid: string;
  content: string;
  created: string;
  senderId: string;
  senderName?: string;
  /** Sender's provider credential (e.g. MD), when they have one. */
  honorific?: string | null;
  threadId?: string;
  urgent: boolean;
  scheduled?: boolean;
  scheduledTime?: string | null;
  attachments?: Attachment[];
}

export const registerSocketEvents = (
  socket: Socket,
  onMessage: (data: MessagePayload) => void,
  onThreadUpdate?: (data: any) => void,
) => {
  // ✅ Receive message event
  const receiveMessageHandler = (data: MessagePayload) => {
    socketLog("📩 Message received:", data);
    onMessage(data);
  };
  socket.on("receive_message", receiveMessageHandler);

  // ✅ Thread list updated event
  const threadListUpdatedHandler = (data: any) => {
    socketLog("🧵 Thread list updated event received:", data);
    socketLog(
      "🧵 Thread list updated - onThreadUpdate callback exists:",
      !!onThreadUpdate,
    );
    if (onThreadUpdate) {
      socketLog("🧵 Calling onThreadUpdate callback");
      onThreadUpdate(data);
    } else {
      console.warn(
        "⚠️ thread_list_updated event received but no callback registered",
      );
    }
  };
  socket.on("thread_list_updated", threadListUpdatedHandler);

  // Debug: Log all socket events (except connection events)
  const allEventsHandler = (eventName: string, ...args: any[]) => {
    if (
      ![
        "connect",
        "disconnect",
        "connect_error",
        "reconnect",
        "reconnect_error",
        "ping",
        "pong",
      ].includes(eventName)
    ) {
      socketLog(`🎯 Socket event '${eventName}':`, args);
    }
  };
  socket.onAny(allEventsHandler);

  return () => {
    socket.off("receive_message", receiveMessageHandler);
    socket.off("thread_list_updated", threadListUpdatedHandler);
    socket.offAny(allEventsHandler);
  };
};

// ✅ Emit join_user_room
export const joinUserRoom = (socket: Socket, userUuid: string) => {
  socketLog("📡 Joining user room:", userUuid);
  socket.emit("join_user_room", userUuid);
};

/**
 * Emit `realtime_subscribe` — the dashboard's entity pushes are fanned out per
 * channel, so the server only forwards them to sockets that asked for the
 * channel by name. This is separate from `join_user_room`: room membership
 * alone does not opt a socket into the realtime entity stream.
 *
 * Must be re-emitted after every reconnect — a reconnected socket is a new
 * server-side socket and carries no subscriptions over.
 */
export const subscribeRealtimeChannels = (socket: Socket, userUuid: string) => {
  const channels = [`user:${userUuid}`];
  socketLog("📡 Subscribing to realtime channels:", channels);
  socket.emit("realtime_subscribe", channels);
};

// ✅ Emit join_thread
export const joinThread = (socket: Socket, threadId: string) => {
  socketLog("📡 Joining thread:", threadId);
  socket.emit("join_thread", threadId);
};

// ✅ Emit send_message
export const sendMessage = (
  socket: Socket,
  payload: { threadId: string; message: string },
) => {
  socketLog("📤 Sending message:", payload);
  socket.emit("send_message", payload);
};

// ✅ Emit send_message with comprehensive payload
export const sendMessageComplete = (
  socket: Socket,
  payload: {
    threadUuid: string;
    senderUuid: string;
    senderName: string;
    content: string;
    scheduled: boolean;
    urgent: boolean;
    scheduledTime: string | null;
    attachments: Attachment[] | null;
  },
) => {
  socketLog("📤 Sending message:", payload);
  socket.emit("send_message", payload);
};

// ✅ Emit thread_messages_read (bulk read)
export const markThreadMessagesRead = (socket: Socket, threadUuid: string) => {
  socketLog("📖 Marking thread messages as read (bulk):", threadUuid);
  socket.emit("thread_messages_read", threadUuid);
};
// export const markThreadMessagesRead = (socket: Socket, threadUuid: string) => {
//   console.log("📖 Marking thread messages as read (bulk):", threadUuid);
//   socket.emit("mark_thread_read", threadUuid); // ✅ Now supported
// };

// Emit mark_read_clinic_user (manual mark-as-read from the dashboard popup)
export const markReadClinicUser = (
  socket: Socket,
  payload: { messageUuid: string; userUuids: string[] },
  onAck?: (response: unknown) => void,
) => {
  socketLog("📖 Marking message as read (clinic user):", payload);
  socket.emit("mark_read_clinic_user", payload, (response: unknown) => {
    socketLog("📖 mark_read_clinic_user ack:", response);
    onAck?.(response);
  });
};

// ✅ Emit mark_message_read (single read)
export const markMessageRead = (socket: Socket, messageUuid: string) => {
  socketLog("📖 Marking message as read (single):", messageUuid);
  socket.emit("mark_message_read", messageUuid);
};

// ─── EHR Dashboard real-time subscriptions ────────────────────────────────────

/**
 * Subscribe to `message_summary_updated` — the server pushes the updated
 * summary for a thread (threadUuid, lastMessage, lastMessageTime, unread
 * counts, sender, …) so the dashboard can patch its message summary panel
 * directly from the event payload instead of refetching the summaries API.
 *
 * Returns an unsubscribe function to be called on component unmount.
 */
export const subscribeMessageSummaryUpdated = (
  socket: Socket,
  onEvent: (data: any) => void,
): (() => void) => {
  const handler = (data: any) => {
    socketLog("📬 [EHR Dashboard] message_summary_updated received:", data);
    onEvent(data);
  };

  socket.on("message_summary_updated", handler);

  return () => {
    socket.off("message_summary_updated", handler);
  };
};

export type DashboardMessageEvent = "receive_message" | "thread_list_updated";

/**
 * Subscribe to incoming message events for the EHR dashboard.
 * Listens to `receive_message` and `thread_list_updated` and calls `onEvent`
 * for either — the caller can then invalidate React Query caches to refresh
 * the message count, urgent messages panel, and message summary panel.
 *
 * The event name and raw payload are forwarded so the caller can tell which
 * thread the event belongs to. The two payloads name the thread differently:
 * `receive_message` carries it as `threadId` (its `uuid` is the *message* id),
 * while `thread_list_updated` carries it as `threadUuid`/`uuid`.
 *
 * Returns an unsubscribe function to be called on component unmount.
 */
export const subscribeDashboardMessageEvents = (
  socket: Socket,
  onEvent: (event: DashboardMessageEvent, data: unknown) => void,
): (() => void) => {
  const receiveMessageHandler = (data: MessagePayload) => {
    socketLog("📩 [EHR Dashboard] receive_message event — triggering refresh");
    onEvent("receive_message", data);
  };

  const threadListUpdatedHandler = (data: unknown) => {
    socketLog(
      "🧵 [EHR Dashboard] thread_list_updated event — triggering refresh",
    );
    onEvent("thread_list_updated", data);
  };

  socket.on("receive_message", receiveMessageHandler);
  socket.on("thread_list_updated", threadListUpdatedHandler);

  socketLog(
    "✅ [EHR Dashboard] Subscribed to receive_message & thread_list_updated",
  );

  return () => {
    socket.off("receive_message", receiveMessageHandler);
    socket.off("thread_list_updated", threadListUpdatedHandler);
    socketLog(
      "🧹 [EHR Dashboard] Unsubscribed from receive_message & thread_list_updated",
    );
  };
};

// ─── EHR Dashboard realtime entity events ─────────────────────────────────────

export interface RealtimeEntityEvent {
  /** Which domain object changed. */
  entity: RealtimeEntity;
  /**
   * What happened to it — e.g. `STATUS_CHANGED`. Left as a plain string rather
   * than a union so a newly added backend action still reaches subscribers;
   * `undefined` when the payload does not carry one.
   */
  action?: string;
  /** The socket event name it arrived on, for logging/diagnostics. */
  eventName: string;
  /** The untouched server payload. */
  payload: unknown;
}

/** Connection/messaging traffic that is never entity-tagged. */
const IGNORED_EVENT_NAMES = new Set([
  "connect",
  "disconnect",
  "connect_error",
  "reconnect",
  "reconnect_error",
  "ping",
  "pong",
  "receive_message",
  "thread_list_updated",
  "message_summary_updated",
]);

/**
 * Reads the first `key` that passes `isValid` from either payload shape the
 * server uses — `{ entity, action }` flat on some events, nested under `data`
 * on others.
 */
const readPayloadField = <T,>(
  payload: unknown,
  key: string,
  isValid: (value: unknown) => value is T,
): T | undefined => {
  if (!payload || typeof payload !== "object") return undefined;
  const record = payload as Record<string, unknown>;
  if (isValid(record[key])) return record[key];
  const nested = record.data;
  if (nested && typeof nested === "object") {
    const nestedValue = (nested as Record<string, unknown>)[key];
    if (isValid(nestedValue)) return nestedValue;
  }
  return undefined;
};

const isString = (value: unknown): value is string => typeof value === "string";

/**
 * Builds the `onAny` handler that turns entity-tagged pushes into
 * `RealtimeEntityEvent`s. `connectSocket` attaches exactly one per socket and
 * fans the events out to every subscriber, so a push is parsed and logged once
 * however many dashboard cards are listening.
 */
export const createRealtimeEntityHandler =
  (onEvent: (event: RealtimeEntityEvent) => void) =>
  (eventName: string, ...args: unknown[]): void => {
    if (IGNORED_EVENT_NAMES.has(eventName)) return;

    const payload = args[0];
    const entity = readPayloadField(payload, "entity", isRealtimeEntity);

    if (!entity) {
      socketDebug(
        `🔍 [Realtime] '${eventName}' carried no known entity — ignored`,
        payload,
      );
      return;
    }

    const action = readPayloadField(payload, "action", isString);

    socketLog(
      `📊 [Realtime] '${eventName}' for entity ${entity}`,
      action ? `action ${action}` : "(no action)",
      payload,
    );
    onEvent({ entity, action, eventName, payload });
  };
